import React, { useState, useEffect } from 'react';
import { FileEdit, X } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const getAuthToken = () => typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;

interface Draft {
  id: string;
  title: string;
  description: string;
  created_at: string;
}

export default function DraftNudgeBanner() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(`draft-nudge-dismissed:${today}`)) {
      setDismissed(true);
      return;
    }
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/engagement/draft-nudges`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.drafts?.length > 0) setDrafts(d.drafts); })
      .catch(() => {});
  }, []);

  if (drafts.length === 0 || dismissed) return null;

  const dismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`draft-nudge-dismissed:${today}`, '1');
    setDismissed(true);
  };

  const daysAgo = (date: string) => {
    const d = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    return d === 1 ? '1 day ago' : `${d} days ago`;
  };

  return (
    <div className="mb-4 rounded-xl bg-amber-500/6 border border-amber-500/15 p-3.5">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-y-2">
        <div className="flex items-center gap-2">
          <FileEdit size={14} className="text-amber-400" />
          <span className="text-xs font-medium text-amber-300">
            You have {drafts.length} unfinished {drafts.length === 1 ? 'draft' : 'drafts'}
          </span>
        </div>
        <button onClick={dismiss} aria-label="Dismiss" className="p-0.5 rounded text-[#9a9aac] hover:text-white transition-colors">
          <X size={14} />
        </button>
      </div>
      <div className="space-y-1.5 ml-[22px]">
        {drafts.map(d => (
          <a
            key={d.id}
            href={`/dashboard/compose?draft=${d.id}`}
            className="flex items-center justify-between py-1 text-xs group"
          >
            <span className="text-[#d0d0e0] group-hover:text-white transition-colors truncate max-w-[200px]">
              {d.title || d.description?.slice(0, 40) || 'Untitled draft'}
            </span>
            <span className="text-[#9a9aac] shrink-0 ml-2">{daysAgo(d.created_at)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
